import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface RemoveImageFromCollectionDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the image to remove from the collection.
   */
  imageId: string | null;
  /**
   * Callback when the image is removed from the collection.
   */
  onRemove: (id: string) => Promise<void>;
}

function RemoveImageFromCollectionDialog(props: RemoveImageFromCollectionDialogProps) {
  const { isOpen = false, onOpenChange, onRemove, imageId, ...other } = props;
  const [isRemoving, setIsRemoving] = React.useState(false);

  const handleOnRemove = React.useCallback(async () => {
    if (imageId) {
      setIsRemoving(true);
      try {
        await onRemove?.(imageId);
      } finally {
        setIsRemoving(false);
      }
    }
  }, [onRemove, imageId]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title showEllipsis={true}>Delete image from collection</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              Are you sure want to delete this image from your collection? This will delete the image from your Asset
              Library.
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={handleClose}
              isDisabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnRemove}
              isLoading={isRemoving}
            >
              Delete
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { RemoveImageFromCollectionDialog };
export type { RemoveImageFromCollectionDialogProps };
